import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

export function HorizontalScrollCue({
  children,
  testId,
}: {
  children: ReactNode;
  testId: string;
}): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const measure = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return;
    const hasOverflow = node.scrollWidth > node.clientWidth + 2;
    setOverflowing(hasOverflow);
    setCanScrollLeft(hasOverflow && node.scrollLeft > 2);
    setCanScrollRight(hasOverflow && node.scrollLeft + node.clientWidth < node.scrollWidth - 2);
  }, []);

  useEffect(() => {
    measure();
    const node = scrollerRef.current;
    const observer = new ResizeObserver(measure);
    if (node) observer.observe(node);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const nudge = (direction: -1 | 1) => {
    scrollerRef.current?.scrollBy({
      left: direction * Math.max(180, scrollerRef.current.clientWidth * 0.7),
      behavior: "smooth",
    });
  };

  return (
    <div className="relative min-w-0" data-testid={`${testId}-container`}>
      {overflowing ? (
        <div className="mb-1 flex items-center justify-between gap-2 px-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:hidden" data-testid={`${testId}-hint`}>
          <span>Swipe or use arrows</span>
          <span className="flex gap-1">
            <button type="button" aria-label="Scroll left" disabled={!canScrollLeft} onClick={() => nudge(-1)} className="rounded-full border border-border/60 p-1 disabled:opacity-30"><ArrowLeft className="h-3 w-3" /></button>
            <button type="button" aria-label="Scroll right" disabled={!canScrollRight} onClick={() => nudge(1)} className="rounded-full border border-border/60 p-1 disabled:opacity-30"><ArrowRight className="h-3 w-3" /></button>
          </span>
        </div>
      ) : null}
      <div
        ref={scrollerRef}
        onScroll={measure}
        className="max-w-full overflow-x-auto overscroll-x-contain pb-2 [scrollbar-color:hsl(var(--muted-foreground))_transparent] [scrollbar-width:thin]"
        data-testid={`${testId}-scroller`}
      >
        {children}
      </div>
      {canScrollRight ? <div aria-hidden="true" className="pointer-events-none absolute bottom-2 right-0 h-10 w-10 bg-gradient-to-l from-background to-transparent sm:hidden" /> : null}
    </div>
  );
}
